import { useBlocksConfig, useSyncer } from "@pathwright/blocks-engine"
import { MediaManager } from "@pathwright/legacy-media-manager"
import { insertQuillImage } from "@pathwright/ui/src/components/quill/blots/ImageBlot"
import { useCallback, useMemo, useRef, type ReactNode } from "react"

type QuillExecuteContext = {
  quill: any
  range: { index: number; length: number }
  deleteTrigger: () => void
}

type SlashCommandLeaf = {
  label: string
  execute: (ctx: QuillExecuteContext) => void
}

export type QuillSlashCommandKey = "image"

export type QuillSlashCommands = Record<QuillSlashCommandKey, SlashCommandLeaf>

/**
 * Slash-command options + host-owned MediaManager for Quill editors.
 * Returns `[slashCommands, mediaManager]` — mount `mediaManager` alongside the editor.
 */
const useQuillSlashCommands = ({
  keys = []
}: {
  keys?: QuillSlashCommandKey[]
} = {}): [QuillSlashCommands | null, ReactNode] => {
  const { contextKey, userID, accountID } = useBlocksConfig()
  const { blocksContext } = useSyncer()
  const mediaStoragePath = blocksContext?.mediaStoragePath

  const openMediaManagerRef = useRef<((event?: any) => void) | null>(null)
  const pendingImageInsertRef = useRef<{
    quill: any
    range: { index: number; length: number }
  } | null>(null)

  const canOpenMediaManager = !!(
    keys?.length > 0 &&
    contextKey &&
    mediaStoragePath
  )

  const slashCommands = useMemo((): QuillSlashCommands | null => {
    if (!canOpenMediaManager || !keys.includes("image")) return null

    return {
      image: {
        label: "Image",
        execute: ({ quill, range, deleteTrigger }) => {
          if (!quill || !range || !openMediaManagerRef.current) return
          deleteTrigger()
          pendingImageInsertRef.current = {
            quill,
            range: { index: range.index, length: 0 }
          }
          requestAnimationFrame(() => {
            openMediaManagerRef.current?.()
          })
        }
      }
    }
  }, [canOpenMediaManager])

  const handleChooseImage = useCallback((media: any) => {
    const pending = pendingImageInsertRef.current
    pendingImageInsertRef.current = null
    if (!pending?.quill || !media?.url) return

    insertQuillImage(pending.quill, pending.range, {
      url: media.url,
      alt: media.name || media.description || ""
    })
  }, [])

  const mediaManager = canOpenMediaManager ? (
    <MediaManager
      contextKey={contextKey}
      mediaStoragePath={mediaStoragePath}
      mediaType="image"
      userID={userID}
      accountID={accountID}
      pickOptions={{ maxFiles: 1 }}
      onChooseMedia={handleChooseImage}
      renderLoading={() => null}
      renderButton={({ onClick }: { onClick?: (event?: any) => void }) => {
        openMediaManagerRef.current = onClick || null
        return null
      }}
    />
  ) : null

  return [slashCommands, mediaManager]
}

export default useQuillSlashCommands
